<template>
  <v-container fluid>
    <!-- Loading -->
    <div v-if="loading" class="text-center pa-10">
      <v-progress-circular
        indeterminate
        color="primary"
        size="50"
      ></v-progress-circular>
      <div class="mt-3 grey--text">กำลังโหลดข้อมูล...</div>
    </div>

    <div v-else-if="matter">
      <!-- ===== BREADCRUMB ===== -->
      <div class="caption grey--text mb-2">
        Incorporation &amp; Corporate &rsaquo; <strong>Detail</strong>
      </div>

      <!-- ===== HEADER ===== -->
      <v-row align="center" class="mb-4">
        <v-col>
          <div class="text-h5 font-weight-bold">
            {{ matter.company_name || matter.client_name || matter.full_name }}
          </div>
          <div class="caption grey--text d-flex align-center" style="gap:8px;">
            <span>{{ matter.entity_type }}</span>
            <span v-if="matter.registration_no"
              >| เลขทะเบียน: <strong>{{ matter.registration_no }}</strong></span
            >
            <v-chip
              :color="matter.status === 'Active' ? 'success' : 'grey lighten-1'"
              :text-color="
                matter.status === 'Active' ? 'white' : 'grey darken-2'
              "
              x-small
              label
            >
              <v-icon left x-small>mdi-circle</v-icon>{{ matter.status }}
            </v-chip>
          </div>
        </v-col>
        <v-col class="d-flex justify-end" style="gap:8px;">
          <v-btn
            outlined
            small
            @click="$router.push('/incorporate-matter-list')"
          >
            <v-icon left small>mdi-arrow-left</v-icon>กลับ
          </v-btn>
          <v-btn outlined small color="orange darken-2" @click="goToEdit">
            <v-icon left small>mdi-pencil</v-icon>Edit
          </v-btn>
          <v-btn color="#0A7EA5" dark small @click="goToNewDocket">
            <v-icon left small>mdi-plus</v-icon>New Docket
          </v-btn>
        </v-col>
      </v-row>

      <!-- ===== TABS ===== -->
      <v-tabs v-model="activeTab" color="#0A7EA5">
        <v-tab>ข้อมูลบริษัท</v-tab>
        <v-tab>Dockets ({{ dockets.length }})</v-tab>
      </v-tabs>
      <v-divider></v-divider>

      <v-tabs-items v-model="activeTab" class="mt-4">
        <!-- ===== TAB 1: ข้อมูลบริษัท ===== -->
        <v-tab-item>
          <!-- COMPANY / INDIVIDUAL INFO -->
          <v-card outlined class="mb-4">
            <div class="section-header">
              🏢 COMPANY INFORMATION
            </div>
            <v-card-text>
              <v-row v-if="!isIndividual">
                <v-col cols="12" md="4">
                  <div class="field-label">Company Name</div>
                  <div class="field-value">
                    {{ matter.company_name || "-" }}
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="field-label">Registration No.</div>
                  <div class="field-value">
                    {{ matter.registration_no || "-" }}
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="field-label">Date of Incorporation</div>
                  <div class="field-value">
                    {{ formatDate(matter.date_of_incorporation) }}
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="field-label">Registered Address</div>
                  <div class="field-value">
                    {{ matter.registered_address || "-" }}
                  </div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="field-label">Branch</div>
                  <div class="field-value">{{ matter.branch || "-" }}</div>
                </v-col>
              </v-row>
              <!-- Individual -->
              <v-row v-else>
                <v-col cols="12" md="4">
                  <div class="field-label">Full Name</div>
                  <div class="field-value">
                    {{ matter.full_name || matter.client_name || "-" }}
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="field-label">Nationality</div>
                  <div class="field-value">{{ matter.nationality || "-" }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="field-label">ID Type</div>
                  <div class="field-value">
                    {{
                      matter.id_type === "national_id"
                        ? "บัตรประชาชน"
                        : "หนังสือเดินทาง"
                    }}
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="field-label">National ID / Passport</div>
                  <div class="field-value">
                    {{ matter.national_id || matter.passport_no || "-" }}
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="field-label">Contact Number</div>
                  <div class="field-value">
                    {{ matter.contact_number || "-" }}
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="field-label">Contact Email</div>
                  <div class="field-value">
                    {{ matter.contact_email || "-" }}
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="field-label">Contact Address</div>
                  <div class="field-value">
                    {{ matter.contact_address || "-" }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- CAPITAL (Company only) -->
          <v-card outlined class="mb-4" v-if="!isIndividual">
            <div class="section-header">💰 CAPITAL</div>
            <v-card-text>
              <v-row>
                <v-col cols="6" md="3">
                  <div class="field-label">Registered Capital</div>
                  <div class="field-value">
                    {{ formatNumber(matter.registered_capital) }} THB
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="field-label">Number of Shares</div>
                  <div class="field-value">
                    {{ formatNumber(matter.number_of_shares) }}
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="field-label">Par Value</div>
                  <div class="field-value">
                    {{ formatNumber(matter.par_value_per_share) }} THB
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="field-label">Paid-up Capital</div>
                  <div class="field-value">
                    {{ formatNumber(matter.paid_up_capital) }} THB
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- SHAREHOLDERS (Company only) -->
          <v-card
            outlined
            class="mb-4"
            v-if="
              !isIndividual &&
                matter.shareholders &&
                matter.shareholders.length > 0
            "
          >
            <div class="section-header">👥 SHAREHOLDERS | ผู้ถือหุ้น</div>
            <v-card-text class="pa-0">
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="bg-colorth tabletextwhite">#</th>
                      <th class="bg-colorth tabletextwhite">Full Name</th>
                      <th class="bg-colorth tabletextwhite">Nationality</th>
                      <th class="bg-colorth tabletextwhite text-right">
                        Ordinary Shares
                      </th>
                      <th class="bg-colorth tabletextwhite text-right">
                        Preferred Shares
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(sh, i) in matter.shareholders" :key="i">
                      <td>{{ i + 1 }}</td>
                      <td>{{ sh.full_name }}</td>
                      <td>{{ sh.nationality || "-" }}</td>
                      <td class="text-right">
                        {{ formatNumber(sh.ordinary_shares) }}
                      </td>
                      <td class="text-right">
                        {{ formatNumber(sh.preferred_shares) }}
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <!-- DIRECTORS (Company only) -->
          <v-card
            outlined
            class="mb-4"
            v-if="
              !isIndividual && matter.directors && matter.directors.length > 0
            "
          >
            <div class="section-header">👔 DIRECTORS | กรรมการบริษัท</div>
            <v-card-text class="pa-0">
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="bg-colorth tabletextwhite">#</th>
                      <th class="bg-colorth tabletextwhite">Full Name</th>
                      <th class="bg-colorth tabletextwhite">Nationality</th>
                      <th class="bg-colorth tabletextwhite">
                        Authorized Director
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(dir, i) in matter.directors" :key="i">
                      <td>{{ i + 1 }}</td>
                      <td>{{ dir.full_name }}</td>
                      <td>{{ dir.nationality || "-" }}</td>
                      <td>
                        <v-chip
                          :color="
                            dir.authorized === 'Yes'
                              ? 'blue lighten-4'
                              : 'grey lighten-3'
                          "
                          x-small
                          label
                          >{{ dir.authorized }}</v-chip
                        >
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>

          <!-- AUDITOR (Company only) -->
          <v-card
            outlined
            class="mb-4"
            v-if="
              !isIndividual &&
                (matter.audit_firm || matter.licensed_auditor_name)
            "
          >
            <div class="section-header">📋 AUDITOR | ผู้สอบบัญชี</div>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="3">
                  <div class="field-label">Audit Firm</div>
                  <div class="field-value">{{ matter.audit_firm || "-" }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="field-label">Licensed Auditor's Name</div>
                  <div class="field-value">
                    {{ matter.licensed_auditor_name || "-" }}
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="field-label">License No.</div>
                  <div class="field-value">
                    {{ matter.auditor_license_no || "-" }}
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="field-label">Fiscal Year End</div>
                  <div class="field-value">
                    {{ formatDate(matter.fiscal_year_end) }}
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="field-label">Auditor National ID</div>
                  <div class="field-value">
                    {{ matter.auditor_national_id || "-" }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- ACCOUNTANT (Company only) -->
          <v-card
            outlined
            class="mb-4"
            v-if="
              !isIndividual &&
                (matter.accountant_firm || matter.accountant_name)
            "
          >
            <div class="section-header">📊 ACCOUNTANT | ผู้ทำบัญชี</div>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <div class="field-label">Accountant Firm</div>
                  <div class="field-value">
                    {{ matter.accountant_firm || "-" }}
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="field-label">Accountant Name</div>
                  <div class="field-value">
                    {{ matter.accountant_name || "-" }}
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="field-label">Accountant National ID</div>
                  <div class="field-value">
                    {{ matter.accountant_national_id || "-" }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- VAT -->
          <v-card
            outlined
            class="mb-4"
            v-if="!isIndividual && matter.vat_issued_on"
          >
            <div class="section-header">🧾 VAT REGISTRATION</div>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <div class="field-label">VAT Issued On</div>
                  <div class="field-value">
                    {{ formatDate(matter.vat_issued_on) }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- FOREIGN BUSINESS -->
          <v-card
            outlined
            class="mb-4"
            v-if="!isIndividual && matter.is_foreign_business"
          >
            <div class="section-header">🌐 FOREIGN BUSINESS</div>
            <v-card-text>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                  v-if="matter.foreign_business_license_file"
                >
                  <div class="field-label">Foreign Business License</div>
                  <v-btn
                    text
                    small
                    color="primary"
                    :href="
                      `${fileIncorporateUrl}/${matter.id}/${matter.foreign_business_license_file}`
                    "
                    target="_blank"
                  >
                    <v-icon left small>mdi-file-pdf-box</v-icon>
                    {{ matter.foreign_business_license_file }}
                  </v-btn>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                  v-if="matter.foreign_business_certificate_file"
                >
                  <div class="field-label">Foreign Business Certificate</div>
                  <v-btn
                    text
                    small
                    color="primary"
                    :href="
                      `${fileIncorporateUrl}/${matter.id}/${matter.foreign_business_certificate_file}`
                    "
                    target="_blank"
                  >
                    <v-icon left small>mdi-file-pdf-box</v-icon>
                    {{ matter.foreign_business_certificate_file }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-tab-item>

        <!-- ===== TAB 2: DOCKETS ===== -->
        <v-tab-item>
          <v-card outlined>
            <v-card-text
              v-if="dockets.length === 0"
              class="text-center pa-8 grey--text"
            >
              ยังไม่มี Docket — คลิก "+ New Docket" เพื่อสร้าง
            </v-card-text>
            <v-simple-table v-else dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="bg-colorth tabletextwhite">#</th>
                    <th class="bg-colorth tabletextwhite">DOC CATEGORY</th>
                    <th class="bg-colorth tabletextwhite">INQUIRY TOPIC</th>
                    <th class="bg-colorth tabletextwhite">DATE RECEIVED</th>
                    <th class="bg-colorth tabletextwhite">DUE DATE</th>
                    <th class="bg-colorth tabletextwhite">STATUS</th>
                    <th class="bg-colorth tabletextwhite">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(docket, i) in dockets" :key="docket.id">
                    <td>{{ i + 1 }}</td>
                    <td>{{ docket.doc_category || "-" }}</td>
                    <td>{{ docket.inquiry_topic || "-" }}</td>
                    <td>{{ formatDate(docket.date_received) }}</td>
                    <td>{{ formatDate(docket.due_date) }}</td>
                    <td>
                      <v-chip
                        :color="getDocketStatusColor(docket.status)"
                        x-small
                        label
                        >{{ docket.status }}</v-chip
                      >
                    </td>
                    <td>
                      <v-btn
                        outlined
                        x-small
                        @click="
                          $router.push(`/incorporate-docket-view/${docket.id}`)
                        "
                        >View</v-btn
                      >
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </div>

    <!-- Not found -->
    <div v-else class="text-center pa-10 grey--text">
      ไม่พบข้อมูล Matter นี้
    </div>
  </v-container>
</template>

<script>
import { server } from "@/services/constants";

// ✅ ไฟล์ incorporate อยู่ที่ production storage port 3001
const INCORPORATE_MATTER_FILE_URL = process.env.VUE_APP_PRODUCTION_STORAGE_URL
  ? `${process.env.VUE_APP_PRODUCTION_STORAGE_URL}/incorporatematter`
  : "http://localhost:3001/incorporatematter";
import {
  getIncorporateMatterById,
  getIncorporateDocketByMatterId,
} from "@/services/api-dtime/api_incorporate";

export default {
  name: "incorporate-matter-view",
  data() {
    return {
      loading: true,
      matter: null,
      dockets: [],
      activeTab: 0,
      fileIncorporateUrl: INCORPORATE_MATTER_FILE_URL,
    };
  },
  computed: {
    isIndividual() {
      return this.matter?.entity_type === "Individual";
    },
  },
  async beforeCreate() {
    this.$store.state.navMenu = true;
  },
  async mounted() {
    this.$hideLoader();
    const id = this.$route.params.id;
    await this.loadMatter(id);
    await this.loadDockets(id);
    this.loading = false;
  },
  methods: {
    async loadMatter(id) {
      try {
        const res = await getIncorporateMatterById(id);
        this.matter = res.data;
        // ถ้า findById return พร้อม dockets แล้ว
        if (this.matter?.dockets) {
          this.dockets = this.matter.dockets;
        }
      } catch (error) {
        console.error("Error loading matter:", error);
      }
    },

    async loadDockets(id) {
      try {
        const res = await getIncorporateDocketByMatterId(id);
        this.dockets = res.data || [];
      } catch (error) {
        console.error("Error loading dockets:", error);
      }
    },

    goToEdit() {
      this.$router.push(`/incorporate-matter-edit/${this.matter.id}`);
    },

    goToNewDocket() {
      this.$router.push(`/incorporate-docket-add?matter_id=${this.matter.id}`);
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString("th-TH", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
      } catch {
        return dateStr;
      }
    },

    formatNumber(val) {
      if (val === null || val === undefined || val === "") return "0";
      return Number(val).toLocaleString("th-TH");
    },

    getDocketStatusColor(status) {
      const map = {
        "In Progress": "blue lighten-4",
        Complete: "green lighten-4",
        Cancelled: "red lighten-4",
      };
      return map[status] || "grey lighten-3";
    },
  },
};
</script>

<style scoped>
.bg-colorth {
  background-color: #8d8d8d !important;
}
.tabletextwhite {
  color: white !important;
}
.section-header {
  font-size: 13px;
  font-weight: 700;
  color: #374151;
  border-left: 4px solid #0a7ea5;
  padding: 10px 16px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}
.field-label {
  font-size: 11px;
  color: #9ca3af;
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.field-value {
  font-size: 14px;
  color: #111827;
  font-weight: 500;
}
</style>
